import React, { useState, useEffect, useRef } from 'react';
import { renderNotificationMessage } from '../../utils/notificationMessage';
import { AppBar, Toolbar, IconButton, Badge, Typography, Box, Paper, Button } from '@mui/material';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import FavoriteIcon from "@mui/icons-material/Favorite";
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { Menu } from '@mui/icons-material';
import { tokens } from '../../theme';
import { useNotifications } from '../../context/NotificationContext';
import NominationPopup from '../Notifications/NominationPopup';
// Custom dropdown menu item component with enhanced styling
const CustomMenuItem = ({ children, onClick, component, to, sx = {} }) => {
    const Component = component || 'div';
    const props = component === Link ? { to } : {};    
    
    return (
        <Component 
            {...props}
            onClick={onClick}
            style={{ 
                padding: '6px 8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '8px',
                margin: '0px 6px',
                transition: 'all 0.2s ease',
                textDecoration: 'none',
                ...sx
            }}
        >
            {children}
        </Component>
    );
};

const TopBar = ({ onMenuClick }) => {
    const { user } = useUser();
    const { 
        notifications, 
        unreadCount, 
        fetchData, 
        approveRequest, 
        rejectRequest,
        acceptNomination,
        rejectNomination,
        openNominationPopup,
        closeNominationPopup,
        showNominationPopup,
        currentNominationId,
        handleNotificationClick
     } = useNotifications();

    const [showNotifications, setShowNotifications] = useState(false);
    const notificationRef = useRef(null);

    // Workspace mark data for the mobile bar (desktop shows it in the sidebar).
    const workspaceName = user?.workspace_name || user?.workspace?.name || user?.company_name || 'Beetsol';
    const workspaceInitial = workspaceName.charAt(0).toUpperCase();
    const location = useLocation();
    const isDashboardRoute = ['/', '/dashboard'].includes(location.pathname);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleApproveNotification = async (notificationId, courseId, learnerId, event) => {
        event.stopPropagation();
        try {
            await approveRequest(notificationId, courseId, learnerId);
            fetchData(); // Refresh notifications
        } catch (error) {
            console.error('Error approving request:', error);
        }
    };

    const handleRejectNotification = async (notificationId, courseId, learnerId, event) => {
        event.stopPropagation();
        try {
            await rejectRequest(notificationId, courseId, learnerId);
            fetchData(); // Refresh notifications
        } catch (error) {
            console.error('Error rejecting request:', error);
        }
    };

    // Add CSS for animations
    const dropdownAnimation = {
        animation: 'dropdownFade 0.18s ease',
        '@keyframes dropdownFade': {
            '0%': { opacity: 0, transform: 'translateY(-8px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' }
        }
    };

    return (
        <>
            {/* DISSOLVED BAR: the header no longer reads as separate chrome.
                Transparent background, no border, no shadow — the bell (and
                learner heart) float over the page canvas, and the dark
                sidebar is the only chrome on screen. Inline overrides win
                over any .topbar-appbar CSS. */}
            {/* At phone width (below sm, where the menu button shows) the bar has the page's background and a hairline, so
                the page scrolls under it instead of the menu button floating over a row's controls (K21.2 pt 30).
                Removed 11 Oct 2026 (K21.2 pt 30): the see-through bar at phone width, where the menu button covered
                the remove button of the row scrolled under it. */}
            <AppBar 
                position="fixed" 
                sx={{ 
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: { xs: 'background.default', sm: 'transparent' },
                    color: 'text.primary',
                    boxShadow: 'none',
                    border: 'none',
                    borderBottom: { xs: `0.5px solid ${tokens.color.borderTertiary}`, sm: 'none' },
                    backgroundImage: 'none',
                    pointerEvents: { xs: 'auto', sm: 'none' },
                    '& .MuiToolbar-root': { pointerEvents: 'none', backgroundColor: 'transparent' },
                    '& button, & a, & [role="button"], & img, & .topbar-interactive': { pointerEvents: 'auto' },
                }}
            >
                <Toolbar className="topbar-toolbar">
                    {/* Workspace mark + NAME — shown on ALL breakpoints. This is
                        now the workspace name's home (the sidebar top shows the
                        mark only, so there is no duplication). On mobile it sits
                        left of the action icons; the sidebar drawer is a separate
                        overlay with its own backdrop, so nothing overlaps. */}
                    {/* 
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: '10px', minWidth: 0, pointerEvents: 'auto' }}>
                        <Box
                            sx={{
                                width: '26px', height: '26px', borderRadius: '7px',
                                background: '#185FA5', color: '#FFFFFF', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '14px', fontWeight: 600, fontFamily: '"DM Sans", sans-serif',
                            }}
                        >
                            {workspaceInitial}
                        </Box>
                        <Box sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {workspaceName}
                        </Box>
                    </Box>
                    */}
                    <Box sx={{ flexGrow: 1 }} /> {/* Spacer */}

                    {/* Search and Notifications group */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pointerEvents: 'auto' }}>
                        {/* Role switcher removed from header — it now lives in the
                            sidebar bottom profile block (single role shows the role;
                            multiple roles open an upward dropdown). */}

                        {/* Bookmarks / favorites — learner role only (content is
                            bookmarkable; heart navigates to saved items). */}
                        {(user?.current_role_id ?? user?.role_id) === 2 && (
                            <IconButton
                                component={Link}
                                to="/learner/favorites"
                                aria-label="Saved content"
                                sx={{
                                    transition: "background-color 0.2s",
                                    "&:hover": { backgroundColor: "rgba(0,0,0,0.08)" },
                                }}
                            >
                                <FavoriteIcon />
                            </IconButton>
                        )}

                        {/* Notification button and dropdown — all roles */}
                        {isDashboardRoute && (
                            <Box sx={{ position: 'relative' }} ref={notificationRef}>
                                <IconButton 
                                    onClick={() => { setShowNotifications(!showNotifications); fetchData(); }}
                                    sx={{ 
                                        transition: 'background-color 0.2s',
                                    }}
                                >
                                    <Badge badgeContent={unreadCount} color="error">
                                        <NotificationsOutlinedIcon />
                                    </Badge>
                                </IconButton>

                                {/* Custom Notifications Dropdown */}
                                {showNotifications && (
                                    <Paper 
                                        sx={{
                                            position: 'absolute',
                                            top: '100%',
                                            right: 0,
                                            width: 320,
                                            maxHeight: '80vh',
                                            overflowY: 'auto',
                                            mt: 1,
                                            borderRadius: 2,
                                            zIndex: 1400,
                                            ...dropdownAnimation
                                        }}
                                        elevation={6}
                                    >       
                                        {notifications && notifications.length > 0 ? (
                                            notifications.map((notification) => (
                                                <Box key={notification.id} sx={{ p: 2 }}>
                                                    {/* The message as text that runs nothing, with its bold and links
                                                        kept (S-26 pt 4). Removed 17 Oct 2026 (S-26 pt 4): inserting it as
                                                        HTML, which ran whatever a learner had typed as their name. */}
                                                    <Box 
                                                        onClick={(e) => handleNotificationClick(notification, e)}
                                                        sx={{ 
                                                            '& a': {
                                                                color: '#111110',
                                                                textDecoration: 'none',
                                                                '&:hover': {
                                                                    textDecoration: 'underline'
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        {renderNotificationMessage(notification.message, { color: '#111110', textDecoration: 'underline' })}
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary">{notification.timeAgo}</Typography>

                                                    {notification.actions && (
                                                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                                                            <Button 
                                                                size="small" 
                                                                variant="outlined" 
                                                                color="primary"
                                                                onClick={(e) => handleApproveNotification(
                                                                    notification.id, 
                                                                    notification.courseId, 
                                                                    notification.learnerId, 
                                                                    e
                                                                )}
                                                            >
                                                                Approve
                                                            </Button>
                                                            <Button 
                                                                size="small" 
                                                                variant="outlined" 
                                                                color="error"
                                                                onClick={(e) => handleRejectNotification(
                                                                    notification.id, 
                                                                    notification.courseId, 
                                                                    notification.learnerId, 
                                                                    e
                                                                )}
                                                            >
                                                                Reject
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </Box>
                                            ))
                                        ) : (
                                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                                <Typography variant="body2" color="text.secondary">No new notifications</Typography>
                                            </Box>
                                        )}
                                        <Box 
                                            component={Link} 
                                            to="/notifications"
                                            onClick={() => setShowNotifications(false)}
                                            sx={{ 
                                                display: 'block',
                                                p: 1.5, 
                                                textAlign: 'center',
                                                color: '#111110',
                                                textDecoration: 'none',
                                                borderTop: '0.5px solid #E8E8E6',
                                                transition: 'background-color 0.2s',
                                                '&:hover': { backgroundColor: '#F7F7F6', textDecoration: 'none' }
                                            }}
                                        >
                                            <Typography variant="body2" fontWeight={500}>Show all notifications</Typography>
                                        </Box>
                                    </Paper>
                                )}
                            </Box>
                        )}
                        {/* Hamburger — EXTREME RIGHT, mobile only (per spec:
                            logo left → … → notifications → hamburger last).
                            Opens the off-canvas sidebar via onMenuClick. */}
                        <IconButton
                            onClick={onMenuClick}
                            aria-label="Open menu"
                            sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: 'text.primary' }}
                        >
                            <Menu />
                        </IconButton>

                        {/* Profile menu removed from header — profile, change
                            password, logout, and role switching now live in the
                            sidebar bottom profile block. */}
                    </Box>
                </Toolbar>
            </AppBar>

            <NominationPopup 
            open={showNominationPopup}
            onClose={closeNominationPopup}
            nominationId={currentNominationId}
            />
        </>
    );
};

// Add this CSS to your global styles or component styles
const style = document.createElement('style');
style.textContent = `
@keyframes dropdownFade {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
}
`;
document.head.appendChild(style);

export default TopBar;